import { useState } from "react";
import { Bot, Check, X, Copy, MessageCircle, Mail, Loader2, Pencil, HelpCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toLocalInput, fromLocalInput, fmtDayLong } from "./agendaUtils";
import { useT, locale } from "../../lib/i18n";

function ConfidenceBar({ value }) {
  const t = useT();
  const pct = Math.round((value || 0) * 100);
  const tone = pct >= 85 ? "bg-emerald-500" : pct >= 60 ? "bg-amber-400" : "bg-slate-400";
  return (
    <div className="flex items-center gap-2" data-testid="proposal-confidence">
      <div className="w-20 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${tone}`} style={{ width: `${pct}%` }} /></div>
      <span className="text-[10px] font-mono-tab text-slate-500">{pct}% {t("zeker")}</span>
    </div>
  );
}

function ProposalCard({ p, clients, onDecided }) {
  const t = useT();
  const [editing, setEditing] = useState(p.ambiguous);
  const [title, setTitle] = useState(p.title || "");
  const [start, setStart] = useState(toLocalInput(p.start));
  const [end, setEnd] = useState(toLocalInput(p.end));
  const [clientId, setClientId] = useState(p.client_id || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const Icon = p.channel === "email" ? Mail : MessageCircle;

  const decide = async (kind) => {
    setBusy(true); setErr("");
    try {
      if (kind === "reject") await api.post(`/agenda/proposals/${p.id}/reject`);
      else await api.post(`/agenda/proposals/${p.id}/accept`, editing
        ? { title, start: fromLocalInput(start), end: fromLocalInput(end), client_id: clientId || null }
        : {});
      onDecided();
    } catch (e) { setErr(formatApiErrorDetail(e?.response?.data?.detail)); setBusy(false); }
  };

  const copyReply = async () => {
    try { await navigator.clipboard.writeText(p.suggested_reply || ""); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* noop */ }
  };

  return (
    <div className={`rounded-xl border p-4 ${p.ambiguous ? "border-amber-200 bg-amber-50/40" : "border-emerald-200 bg-emerald-50/30"}`} data-testid={`proposal-${p.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-600 shrink-0"><Icon size={14} /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-slate-900 text-sm">{p.sender_name || p.sender_email}</span>
            {p.sender_entity && <span className="text-xs text-slate-500">· {p.sender_entity}</span>}
            {p.contact_name && <span className="pm-pill pm-pill-blue text-[10px]">{t("contact gekoppeld")}</span>}
            {p.ambiguous && <span className="pm-pill pm-pill-amber text-[10px] flex items-center gap-1"><HelpCircle size={10} /> {t("geen concrete tijd")}</span>}
          </div>
          <div className="text-xs text-slate-600 mt-1 italic">“{p.snippet}”</div>
          <div className="mt-2"><ConfidenceBar value={p.confidence} /></div>

          {!editing ? (
            <div className="mt-3 text-sm" data-testid="proposal-summary">
              <span className="font-bold">{p.title}</span>
              {p.start && <span className="text-slate-600"> · {fmtDayLong(p.start)}{p.end ? ` – ${new Date(p.end).toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" })}` : ""}</span>}
            </div>
          ) : (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="proposal-edit-form">
              <input className="pm-input sm:col-span-2" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("Titel")} data-testid="proposal-edit-title" />
              <input type="datetime-local" className="pm-input" value={start} onChange={(e) => setStart(e.target.value)} data-testid="proposal-edit-start" />
              <input type="datetime-local" className="pm-input" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="proposal-edit-end" />
              <select className="pm-input sm:col-span-2" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="proposal-edit-client">
                <option value="">{t("— geen dossier —")}</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
              </select>
            </div>
          )}

          {p.ambiguous && p.suggested_reply && (
            <div className="mt-3 rounded-lg bg-white border border-slate-200 p-3" data-testid="proposal-suggested-reply">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">{t("Voorgestelde wedervraag")}</div>
              <div className="text-xs text-slate-700">{p.suggested_reply}</div>
              <button onClick={copyReply} className="mt-2 text-[11px] font-bold text-emerald-700 flex items-center gap-1" data-testid="proposal-copy-reply">
                <Copy size={11} /> {copied ? t("Gekopieerd") : t("Kopieer antwoord")}
              </button>
            </div>
          )}
          {err && <div className="text-xs text-red-600 mt-2" data-testid="proposal-error">{err}</div>}
          <div className="flex items-center gap-2 mt-3">
            <button onClick={() => decide("accept")} disabled={busy || (editing && !start)} className="pm-btn-primary h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1" data-testid="proposal-accept">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} {editing ? t("Opslaan in agenda") : t("Goedkeuren")}
            </button>
            {!editing && (
              <button onClick={() => setEditing(true)} className="pm-btn-secondary h-8 px-3 rounded-lg text-xs font-bold flex items-center gap-1" data-testid="proposal-edit"><Pencil size={12} /> {t("Aanpassen")}</button>
            )}
            <button onClick={() => decide("reject")} disabled={busy} className="pm-btn-secondary h-8 px-3 rounded-lg text-xs font-bold text-red-600 flex items-center gap-1" data-testid="proposal-reject"><X size={12} /> {t("Afwijzen")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProposalsPanel({ proposals, clients, onDecided, onScan, scanning, history }) {
  const t = useT();
  return (
    <div className="pm-card p-5" data-testid="agenda-proposals-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 flex items-center gap-1"><Bot size={11} /> {t("AI-herkenning")}</div>
          <h2 className="font-display text-xl font-bold tracking-tight">{t("Afspraakvoorstellen")} · {proposals.length}</h2>
        </div>
        <button onClick={onScan} disabled={scanning} className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5" data-testid="agenda-scan-now">
          {scanning ? <Loader2 size={12} className="animate-spin" /> : <Bot size={12} />} {t("Scan inbox nu")}
        </button>
      </div>
      {proposals.length === 0 && (
        <div className="text-sm text-slate-500 py-8 text-center" data-testid="agenda-proposals-empty">
          {t("Geen openstaande voorstellen. De AI scant elke 5 minuten je binnenkomende e-mails en berichten op afspraakverzoeken.")}
        </div>
      )}
      <div className="space-y-3">
        {proposals.map((p) => <ProposalCard key={p.id} p={p} clients={clients} onDecided={onDecided} />)}
      </div>
      {history.length > 0 && (
        <div className="mt-6" data-testid="agenda-proposals-history">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">{t("Recent verwerkt")}</div>
          <div className="space-y-1">
            {history.slice(0, 6).map((h) => (
              <div key={h.id} className="flex items-center gap-2 text-xs text-slate-600">
                <span className={`pm-pill text-[10px] ${h.status === "rejected" ? "pm-pill-red" : "pm-pill-green"}`}>{h.status === "auto_added" ? t("automatisch") : h.status === "accepted" ? t("goedgekeurd") : t("afgewezen")}</span>
                <span className="font-semibold">{h.title}</span><span className="text-slate-400">· {h.sender_name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
